import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDateTime, formatNumber } from '@awj/i18n';
import { Button, useToast } from '@awj/ui';

import { Icon } from './Icon.tsx';
import { panelStyle, usePopover } from './usePopover.ts';
import { useI18n } from '~/i18n/index.tsx';
import {
  MAX_PDF_ROWS,
  downloadWorkbook,
  fileName,
  pdfTable,
  saveFile,
  type ExportColumn,
} from '~/lib/export.ts';
import { admin } from '~/lib/queries.ts';

type Format = 'excel' | 'pdf';

/**
 * Downloads a table as an Excel workbook or a PDF document.
 *
 * One control with a short menu rather than a button per format: it sits beside each page's main
 * action, and two near-identical buttons there would outweigh the action itself. The reference
 * panel's own ExportMenu made the same call.
 *
 * `rows` is asked for only once a format is chosen, because a paged list has to read every page
 * to answer it; a table that already holds all its rows simply hands them back.
 *
 * Outlined where it shares a header with the page's main action, quiet where it sits in a card's
 * head beside the content it exports.
 */
export function ExportMenu<Row>({
  file,
  sheet,
  title = sheet,
  columns,
  rows,
  variant = 'secondary',
  hint,
  disabled,
}: {
  file: string;
  sheet: string;
  /** The PDF's heading, where the sheet's short name would not say whose table this is. */
  title?: string;
  columns: ExportColumn<Row>[];
  rows: () => Row[] | Promise<Row[]>;
  variant?: 'secondary' | 'ghost';
  /** Said on hover — for a paged list, that the file holds more than the page. */
  hint?: string;
  disabled?: boolean;
}) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const { open, setOpen, close, pos, rootRef, panelRef, id } = usePopover({ align: 'end' });

  // Opening puts the caret on the first format, so the arrow keys work straight away.
  useEffect(() => {
    if (open && pos) {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [open, pos, panelRef]);

  // Choosing a format closes the menu and disables the trigger while the file is made, which
  // drops focus to the page. It goes back to the trigger once the trigger can take it again.
  const wasBusy = useRef(false);

  useEffect(() => {
    if (wasBusy.current && !busy) {
      rootRef.current?.querySelector('button')?.focus();
    }

    wasBusy.current = busy;
  }, [busy, rootRef]);

  async function run(format: Format) {
    close();
    setBusy(true);

    try {
      const all = await rows();

      if (format === 'excel') {
        await downloadWorkbook({ file, sheet, columns, rows: all, locale });

        return;
      }

      if (all.length > MAX_PDF_ROWS) {
        toast.show({
          title: t('export.failed'),
          message: t('export.tooManyRows', { max: formatNumber(locale, MAX_PDF_ROWS) }),
          tone: 'danger',
        });

        return;
      }

      const pdf = await admin().exportTablePdf({
        title,
        subtitle: t('common.totalRows', { count: formatNumber(locale, all.length) }),
        footer: t('export.exportedAt', { at: formatDateTime(locale, new Date()) }),
        rightToLeft: locale === 'ar',
        ...pdfTable(columns, all, locale),
      });

      saveFile(pdf, fileName(file, 'pdf'));
    } catch {
      toast.show({ title: t('export.failed'), message: t('export.failedHint'), tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')];
    const current = items.indexOf(document.activeElement as HTMLElement);
    const move = (index: number) => items[(index + items.length) % items.length]?.focus();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(current + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(current - 1);
        break;
      case 'Home':
        event.preventDefault();
        move(0);
        break;
      case 'End':
        event.preventDefault();
        move(items.length - 1);
        break;
      case 'Tab':
        // A menu is one stop in the tab order; leaving it closes it.
        close();
        break;
      default:
        break;
    }
  }

  return (
    <div className="menu" ref={rootRef}>
      <Button
        variant={variant}
        size="sm"
        loading={busy}
        loadingLabel={t('export.working')}
        iconStart={<Icon name="download" size={16} />}
        iconEnd={<Icon name="chevronDown" size={14} className="export-menu__caret" />}
        disabled={disabled}
        title={hint}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
      >
        {t('export.export')}
      </Button>

      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={id}
              role="menu"
              aria-label={t('export.export')}
              className="menu__panel export-menu__panel"
              style={panelStyle(pos)}
              onKeyDown={onMenuKeyDown}
            >
              {/* Each format in its own file type's colour — Excel's green, PDF's red. */}
              <button
                type="button"
                role="menuitem"
                className="menu__item export-menu__item export-menu__item--excel"
                onClick={() => run('excel')}
              >
                <Icon name="fileExcel" className="menu__item-icon" size={20} />
                <span>{t('export.excel')}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="menu__item export-menu__item export-menu__item--pdf"
                onClick={() => run('pdf')}
              >
                <Icon name="filePdf" className="menu__item-icon" size={20} />
                <span>{t('export.pdf')}</span>
              </button>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
